import React from 'react';
import './LegalPages.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 23, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Cruise Spotter Connect, LLC ("we," "our," or "us") respects your privacy and is
            committed to protecting your personal information. This Privacy Policy explains how we
            collect, use, disclose, and safeguard information when you use our services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>From Travel Agents</h3>
          <ul>
            <li>Name, business name, and contact information</li>
            <li>Travel advisor license information</li>
            <li>Payment information (processed securely through Stripe)</li>
            <li>Call activity and performance data</li>
            <li>Account preferences and settings</li>
          </ul>

          <h3>From Consumers</h3>
          <ul>
            <li>Phone numbers when they call our routing line</li>
            <li>Call audio recordings for quality assurance and billing verification</li>
            <li>Call metadata (duration, timestamp, routing information)</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <ul>
            <li>IP addresses and browser information</li>
            <li>Usage data and analytics</li>
            <li>Device information</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li><strong>Provide Our Services:</strong> Route calls between consumers and travel agents</li>
            <li><strong>Billing & Payment:</strong> Process payments and generate invoices</li>
            <li><strong>Quality Assurance:</strong> Review call recordings to ensure service quality</li>
            <li><strong>Dispute Resolution:</strong> Resolve billing disputes and verify call quality</li>
            <li><strong>Communication:</strong> Send service updates, billing notifications, and support</li>
            <li><strong>Improvement:</strong> Analyze usage to improve our platform</li>
            <li><strong>Compliance:</strong> Meet legal and regulatory obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Call Recording Disclosure</h2>
          <p>
            <strong>Important:</strong> All calls routed through our platform are recorded for quality
            assurance, training, and billing verification purposes. By using our service:
          </p>
          <ul>
            <li>Travel agents consent to having their calls recorded</li>
            <li>Consumers are informed at the beginning of calls that the conversation may be recorded</li>
            <li>Call recordings are stored securely and retained for 90 days</li>
            <li>Recordings may be reviewed for dispute resolution or quality purposes</li>
          </ul>
        </section>

        <section>
          <h2>5. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share information in the following circumstances:</p>
          <ul>
            <li><strong>With Travel Agents:</strong> Consumer phone numbers are shared with assigned agents</li>
            <li><strong>Service Providers:</strong> Third-party services that help us operate (Stripe for payments, Aircall for routing)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Consent:</strong> When you give us permission to share</li>
          </ul>
        </section>

        <section>
          <h2>6. How We Protect Your Information</h2>
          <p>We implement appropriate technical and organizational measures to protect your data:</p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure storage with access controls</li>
            <li>Regular security audits and updates</li>
            <li>Employee training on data protection</li>
            <li>PCI-DSS compliant payment processing through Stripe</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure. While we strive to
            protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <ul>
            <li><strong>Call Recordings:</strong> Retained for 90 days, then permanently deleted</li>
            <li><strong>Account Information:</strong> Retained while your account is active and for 7 years after closure for tax/legal purposes</li>
            <li><strong>Billing Records:</strong> Retained for 7 years per financial record-keeping requirements</li>
            <li><strong>Analytics Data:</strong> Retained in aggregate form indefinitely</li>
          </ul>
        </section>

        <section>
          <h2>8. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (service emails still required)</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:hello@cruisespotterconnect.com">hello@cruisespotterconnect.com</a>
          </p>
        </section>

        <section>
          <h2>9. Consumer Rights</h2>
          <p>If you are a consumer who called our routing line:</p>
          <ul>
            <li>You may request deletion of your phone number and call recordings</li>
            <li>You may request to not be contacted by the agent you were connected with</li>
            <li>Call recordings are automatically deleted after 90 days</li>
          </ul>
          <p>
            Contact us at{' '}
            <a href="mailto:hello@cruisespotterconnect.com">hello@cruisespotterconnect.com</a>
          </p>
        </section>

        <section>
          <h2>10. Cookies and Tracking</h2>
          <p>
            We use cookies and similar tracking technologies to improve your experience on our website.
            You can control cookies through your browser settings. Note that disabling cookies may
            affect website functionality.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the
            privacy practices of these external sites. Please review their privacy policies.
          </p>
        </section>

        <section>
          <h2>12. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under 18. We do not knowingly collect
            information from children. If you believe we have collected information from a child,
            please contact us immediately.
          </p>
        </section>

        <section>
          <h2>13. State-Specific Privacy Rights</h2>
          <h3>California Residents (CCPA)</h3>
          <p>California residents have additional rights under the California Consumer Privacy Act:</p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to know if personal information is sold or disclosed</li>
            <li>Right to opt-out of sale of personal information (we do not sell information)</li>
            <li>Right to deletion</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>

          <h3>Other State Laws</h3>
          <p>
            Residents of other states may have similar rights under state privacy laws. Contact us
            to exercise any applicable rights.
          </p>
        </section>

        <section>
          <h2>14. International Users</h2>
          <p>
            Our services are based in the United States. If you access our services from outside the
            U.S., your information will be transferred to and processed in the United States, which
            may have different data protection laws than your country.
          </p>
        </section>

        <section>
          <h2>15. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material
            changes by email or through a prominent notice on our website. Continued use of our
            services after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>For privacy-related questions, concerns, or requests, contact us:</p>
          <div className="contact-info">
            <p><strong>Cruise Spotter Connect, LLC</strong></p>
            <p>Email: <a href="mailto:hello@cruisespotterconnect.com">hello@cruisespotterconnect.com</a></p>
            <p>Hours: Monday-Friday, 9 AM - 6 PM EST</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
